import { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReplayIcon from "@mui/icons-material/Replay";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { propertyOrdersRequest, unwrapOrders } from "services/propertyOrders";

const statusValue = (item) => item?.status || item?.orderStatus || item?.state || "Unknown";
const orderIdValue = (order) => order?.orderId ?? order?.id;
const orderSteps = (order) => order?.steps || order?.orderSteps || order?.processingSteps || [];
const stepJobs = (step) => step?.jobs || step?.stepJobs || step?.processingJobs || [];

const orderUserId = (order) =>
  order?.userId ??
  order?.user?.id ??
  order?.user?.userId ??
  order?.owner?.id ??
  order?.requestedBy?.id ??
  order?.createdBy?.id;

const isFailed = (item) => item?.hasErrored === true || /fail|error/i.test(String(statusValue(item)));
const hasErrorResult = (item) => typeof item?.hasErrored === "boolean";

const formatLabel = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  if (/date|time|created|updated|started|completed|finished|failed|checked/i.test(key)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  }
  return String(value);
};

const jsonValue = (key, value) => {
  if (typeof value === "object" && value !== null) return value;
  if (!/json|response/i.test(key) || typeof value !== "string") return null;

  try {
    const parsedValue = JSON.parse(value);
    return typeof parsedValue === "object" && parsedValue !== null ? parsedValue : null;
  } catch (error) {
    return null;
  }
};

const statusColor = (status) => {
  if (/fail|error/i.test(status)) return "error";
  if (/complete|success|finished/i.test(status)) return "success";
  if (/progress|running|processing/i.test(status)) return "info";
  if (/pending|queued|waiting/i.test(status)) return "warning";
  return "text";
};

const detailEntries = (item, excludedKeys) =>
  Object.entries(item || {}).filter(
    ([key, value]) =>
      !excludedKeys.includes(key) &&
      value !== null &&
      value !== undefined
  );

function ResultIndicator({ item, label }) {
  if (!hasErrorResult(item)) return null;

  const errored = item.hasErrored;
  return (
    <MDBox display="flex" alignItems="center" gap={0.5} color={errored ? "error.main" : "success.main"}>
      {errored ? (
        <ErrorIcon fontSize="small" color="error" />
      ) : (
        <CheckCircleIcon fontSize="small" color="success" />
      )}
      <MDTypography variant="caption" color={errored ? "error" : "success"} fontWeight="bold">
        {label} {errored ? "failed" : "succeeded"}
      </MDTypography>
    </MDBox>
  );
}

ResultIndicator.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  label: PropTypes.string.isRequired,
};

function JsonDetail({ label, value }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const formatted = JSON.stringify(value, null, 2);

  const copyValue = async () => {
    await navigator.clipboard.writeText(formatted);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <MDBox minWidth={0}>
      <MDTypography display="block" variant="caption" color="text">
        {label}
      </MDTypography>
      <MDBox display="flex" alignItems={expanded ? "flex-start" : "center"} gap={0.5} minWidth={0}>
        <MDTypography
          component="pre"
          variant="button"
          fontWeight="medium"
          sx={{
            flex: 1,
            minWidth: 0,
            m: 0,
            overflow: expanded ? "auto" : "hidden",
            textOverflow: expanded ? "clip" : "ellipsis",
            whiteSpace: expanded ? "pre-wrap" : "nowrap",
            overflowWrap: "anywhere",
          }}
        >
          {expanded ? formatted : formatted.replace(/\s+/g, " ")}
        </MDTypography>
        <Tooltip title={copied ? "Copied" : "Copy JSON"}>
          <IconButton size="small" aria-label={`Copy ${label}`} onClick={copyValue}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? "Collapse JSON" : "Expand JSON"}>
          <IconButton
            size="small"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
          </IconButton>
        </Tooltip>
      </MDBox>
    </MDBox>
  );
}

JsonDetail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

function DetailGrid({ item, excludedKeys }) {
  const entries = detailEntries(item, excludedKeys);
  if (entries.length === 0) return null;

  return (
    <Grid container spacing={1.5} mt={0.25}>
      {entries.map(([key, value]) => (
        <Grid item xs={12} sm={6} lg={4} key={key}>
          {jsonValue(key, value) ? (
            <JsonDetail label={formatLabel(key)} value={jsonValue(key, value)} />
          ) : (
            <>
              <MDTypography display="block" variant="caption" color="text">
                {formatLabel(key)}
              </MDTypography>
              <MDTypography
                display="block"
                variant="button"
                fontWeight="medium"
                sx={{ overflowWrap: "anywhere" }}
              >
                {formatValue(key, value)}
              </MDTypography>
            </>
          )}
        </Grid>
      ))}
    </Grid>
  );
}

DetailGrid.propTypes = {
  item: PropTypes.objectOf(PropTypes.any).isRequired,
  excludedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function UserOrders({ userId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryOrder, setRetryOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [message, setMessage] = useState("");
  const sessionId = useSelector((state) => state.login.sessionId);
  const navigate = useNavigate();

  const handleError = (requestError) => {
    if (requestError.status === 401 || requestError.status === 403) {
      navigate("/authentication/sign-in");
      return;
    }
    setError(requestError.message || "Orders could not be loaded.");
  };

  const loadOrders = async () => {
    if (!userId) return;

    setLoading(true);
    setError("");
    try {
      const response = await propertyOrdersRequest("all", sessionId);
      const userOrders = unwrapOrders(response).filter(
        (order) => String(orderUserId(order)) === String(userId)
      );
      setOrders(userOrders);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, userId]);

  const confirmRetry = async () => {
    const orderId = orderIdValue(retryOrder);
    if (orderId === null || orderId === undefined) return;

    setRetrying(true);
    setError("");
    try {
      await propertyOrdersRequest(`${orderId}/retry`, sessionId, { method: "POST" });
      setRetryOrder(null);
      setMessage(`Retry requested for order ${orderId}.`);
      await loadOrders();
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Card sx={{ boxShadow: "none", mt: 3 }}>
      <MDBox p={2}>
        <MDTypography variant="h6" fontWeight="medium">
          Property orders
        </MDTypography>
        <MDTypography variant="caption" color="text">
          Order status, processing steps, and failure details for this user.
        </MDTypography>
      </MDBox>

      <MDBox px={2} pb={2}>
        {loading && (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress size={30} color="info" />
          </MDBox>
        )}

        {error && (
          <MDTypography display="block" variant="button" color="error" mb={2}>
            {error}
          </MDTypography>
        )}

        {message && (
          <MDTypography display="block" variant="button" color="success" mb={2}>
            {message}
          </MDTypography>
        )}

        {!loading && !error && orders.length === 0 && (
          <MDTypography variant="button" color="text">
            No property orders were found for this user.
          </MDTypography>
        )}

        {!loading &&
          orders.map((order) => {
            const orderId = orderIdValue(order);
            const status = String(statusValue(order));
            const steps = orderSteps(order);
            const failed =
              isFailed(order) ||
              steps.some((step) => isFailed(step) || stepJobs(step).some(isFailed));

            return (
              <Accordion
                key={orderId ?? JSON.stringify(order)}
                disableGutters
                sx={(theme) => ({
                  border: 1,
                  borderLeft: 4,
                  borderColor: hasErrorResult(order)
                    ? theme.palette[order.hasErrored ? "error" : "success"].main
                    : theme.palette.divider,
                  backgroundColor: hasErrorResult(order)
                    ? alpha(theme.palette[order.hasErrored ? "error" : "success"].main, 0.035)
                    : "transparent",
                  boxShadow: "none",
                  mb: 1.5,
                })}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <MDBox
                    width="100%"
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={1}
                    pr={1}
                  >
                    <MDBox display="flex" flexDirection="column" alignItems="flex-start" gap={0.25}>
                      <MDTypography variant="button" fontWeight="bold">
                        Order {orderId ?? "Unknown"}
                      </MDTypography>
                      <ResultIndicator item={order} label="Order" />
                      <MDTypography display="block" variant="caption" color="text">
                        {steps.length} processing {steps.length === 1 ? "step" : "steps"}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color={statusColor(status)} fontWeight="bold">
                      {status}
                    </MDTypography>
                  </MDBox>
                </AccordionSummary>
                <AccordionDetails>
                  <DetailGrid
                    item={order}
                    excludedKeys={[
                      "id",
                      "orderId",
                      "status",
                      "orderStatus",
                      "state",
                      "hasErrored",
                      "steps",
                      "orderSteps",
                      "processingSteps",
                      "user",
                      "owner",
                      "requestedBy",
                      "createdBy",
                    ]}
                  />

                  <MDBox mt={3}>
                    <MDTypography variant="button" fontWeight="bold">
                      Processing steps
                    </MDTypography>
                    {steps.length === 0 && (
                      <MDTypography display="block" variant="caption" color="text" mt={1}>
                        No step information was returned for this order.
                      </MDTypography>
                    )}
                    {steps.map((step, index) => {
                      const stepStatus = String(statusValue(step));
                      const stepName = step.name || step.stepName || step.type || `Step ${index + 1}`;
                      const jobs = stepJobs(step);

                      return (
                        <MDBox
                          key={step.id ?? step.stepId ?? `${stepName}-${index}`}
                          mt={1.5}
                          p={2}
                          borderRadius="md"
                          sx={(theme) => ({
                            borderLeft: 3,
                            borderColor: hasErrorResult(step)
                              ? theme.palette[step.hasErrored ? "error" : "success"].main
                              : theme.palette.divider,
                            backgroundColor: hasErrorResult(step)
                              ? alpha(
                                  theme.palette[step.hasErrored ? "error" : "success"].main,
                                  0.055
                                )
                              : theme.palette.grey[100],
                          })}
                        >
                          <MDBox
                            display="flex"
                            flexDirection={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            gap={1}
                          >
                            <MDTypography variant="button" fontWeight="bold">
                              {stepName}
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" gap={1.5}>
                              <ResultIndicator item={step} label="Step" />
                              <MDTypography
                                variant="caption"
                                color={statusColor(stepStatus)}
                                fontWeight="bold"
                              >
                                {stepStatus}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <DetailGrid
                            item={step}
                            excludedKeys={[
                              "id",
                              "stepId",
                              "name",
                              "stepName",
                              "type",
                              "status",
                              "orderStatus",
                              "state",
                              "hasErrored",
                              "jobs",
                              "stepJobs",
                              "processingJobs",
                            ]}
                          />
                          {jobs.length > 0 && (
                            <MDBox mt={2}>
                              <MDTypography display="block" variant="caption" color="text" mb={0.75}>
                                Jobs
                              </MDTypography>
                              {jobs.map((job, jobIndex) => {
                                const jobName =
                                  job.name || job.jobName || job.type || `Job ${jobIndex + 1}`;

                                return (
                                  <MDBox
                                    key={job.id ?? job.jobId ?? `${jobName}-${jobIndex}`}
                                    py={1}
                                    sx={{ borderTop: 1, borderColor: "divider" }}
                                  >
                                    <MDBox
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <MDTypography variant="button" fontWeight="medium">
                                        {jobName}
                                      </MDTypography>
                                      <ResultIndicator item={job} label="Job" />
                                    </MDBox>
                                    <DetailGrid
                                      item={job}
                                      excludedKeys={[
                                        "id",
                                        "jobId",
                                        "name",
                                        "jobName",
                                        "type",
                                        "hasErrored",
                                      ]}
                                    />
                                  </MDBox>
                                );
                              })}
                            </MDBox>
                          )}
                        </MDBox>
                      );
                    })}
                  </MDBox>

                  {failed && orderId !== null && orderId !== undefined && (
                    <MDBox display="flex" justifyContent="flex-end" mt={2}>
                      <MDButton color="error" variant="outlined" onClick={() => setRetryOrder(order)}>
                        <ReplayIcon fontSize="small" sx={{ mr: 0.75 }} /> Retry failed order
                      </MDButton>
                    </MDBox>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </MDBox>

      <Dialog open={Boolean(retryOrder)} onClose={() => setRetryOrder(null)}>
        <DialogTitle>Retry failed order?</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            This will retry order {orderIdValue(retryOrder)} and may restart its failed processing
            steps.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setRetryOrder(null)}>
            Cancel
          </MDButton>
          <MDButton color="error" disabled={retrying} onClick={confirmRetry}>
            {retrying ? "Retrying..." : "Retry order"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

UserOrders.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

UserOrders.defaultProps = {
  userId: null,
};

export default UserOrders;